import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../api';

const C = {
  bg: '#0a0e27', panel: '#141932', card2: '#1a2045', border: '#252b4a',
  text: '#e4e6ed', muted: '#8b92a8', accent: '#00d4ff',
  ok: '#22c55e', warn: '#fbbf24', err: '#ef4444',
};

// Cohort heatmap cell color: retention %-ből gradient (0 = lightest, 100 = strongest)
function retentionColor(pct) {
  if (pct == null) return 'transparent';
  if (pct === 0) return '#1a2045';
  // hsl: kékes-zöld skálán
  const intensity = Math.min(1, pct / 60); // 60%+ = max color
  const hue = 175; // teal
  const sat = 30 + intensity * 50;
  const light = 35 - intensity * 15; // sötétebb = magasabb retention
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

function fmtUsd(v) {
  if (v == null) return '—';
  return '$' + v.toFixed(2);
}

export default function BusinessIntel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [citySort, setCitySort] = useState('revenue'); // revenue / users / paidConv

  const load = async (refresh = false) => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch('/api/business-intel' + (refresh ? '?refresh=1' : ''), { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'HTTP ' + r.status);
      setData(d);
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Betöltés… ez 10-30 másodperc, mert teljes user + transaction kollekciót olvas.</div>;
  }
  if (err && !data) {
    return <div style={{ padding: 20, background: '#3a1f1f', border: '1px solid ' + C.err, borderRadius: 4, color: C.err }}>⚠️ {err}</div>;
  }
  if (!data) return null;

  const sub = data.subscriptions;
  const maxOffset = Math.max(...data.cohorts.map(c => c.retention.length));

  // havi új előfizetők chart-adat
  const monthlyChartData = data.months.map(m => ({
    month: m.slice(5), // MM
    fullMonth: m,
    count: sub.monthlyNewPaying[m] || 0,
  }));

  // városok rendezése
  const cities = data.cities.slice();
  if (citySort === 'revenue') cities.sort((a, b) => b.revenueUsd - a.revenueUsd);
  else if (citySort === 'users') cities.sort((a, b) => b.users - a.users);
  else if (citySort === 'paidConv') cities.sort((a, b) => b.paidConvPct - a.paidConvPct);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>💼 Business Intelligence</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12, color: C.muted }}>
          {data.cached && <span>💾 cache-elt (10 perc TTL)</span>}
          <span>Generálva: {new Date(data.generatedAt).toLocaleString('hu-HU')}</span>
          <button className="btn" onClick={() => load(true)} disabled={loading}
            style={{ padding: '6px 12px', fontSize: 12 }}>
            {loading ? 'Frissítés…' : '🔄 Frissítés (cache flush)'}
          </button>
        </div>
      </div>

      {/* ── KPI sáv ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        <div className="card">
          <div style={{ fontSize: 11, color: C.muted }}>Aktív előfizetők</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: C.ok }}>{sub.activeNow}</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>most</div>
        </div>
        <div className="card">
          <div style={{ fontSize: 11, color: C.muted }}>Valaha fizetett</div>
          <div style={{ fontSize: 26, fontWeight: 700 }}>{sub.everPaid}</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>kumulatív</div>
        </div>
        <div className="card">
          <div style={{ fontSize: 11, color: C.muted }}>Snapshot churn %</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: sub.churnRateSnapshot > 50 ? C.warn : C.text }}>{sub.churnRateSnapshot}%</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>(everPaid − activeNow) / everPaid</div>
        </div>
        <div className="card">
          <div style={{ fontSize: 11, color: C.muted }}>Összes bevétel</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: C.accent }}>{fmtUsd(sub.totalRevenueUsd)}</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>kumulatív IAP</div>
        </div>
        <div className="card">
          <div style={{ fontSize: 11, color: C.muted }}>LTV fizető user-re</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: C.accent }}>{fmtUsd(sub.avgRevenuePerPayingUser)}</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>átlag fizető user</div>
        </div>
      </div>

      {/* ── COHORT RETENTION HEATMAP ─────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, color: C.muted, margin: '0 0 12px 0' }}>📊 Cohort retention (12 hónap)</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ fontSize: 11, borderCollapse: 'collapse', minWidth: '100%' }}>
            <thead>
              <tr style={{ color: C.muted }}>
                <th style={{ padding: '4px 8px', textAlign: 'left', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' }}>Cohort</th>
                <th style={{ padding: '4px 8px', textAlign: 'right', borderBottom: '1px solid ' + C.border }}>Méret</th>
                {Array.from({ length: maxOffset }, (_, i) => (
                  <th key={i} style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid ' + C.border, minWidth: 50 }}>M+{i}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.cohorts.map(c => (
                <tr key={c.cohort}>
                  <td style={{ padding: '4px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>{c.cohort}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: C.muted }}>{c.size}</td>
                  {Array.from({ length: maxOffset }, (_, i) => {
                    const r = c.retention[i];
                    if (!r) return <td key={i} style={{ padding: 0 }}></td>;
                    return (
                      <td key={i} title={`${r.count} aktív user`}
                        style={{ padding: '6px 4px', textAlign: 'center', background: retentionColor(r.pct), color: r.pct > 30 ? '#fff' : C.text, fontSize: 10, fontWeight: r.pct > 20 ? 600 : 400 }}>
                        {r.pct > 0 ? r.pct.toFixed(1) + '%' : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: C.muted }}>
          💡 Aktív = van legalább 1 <code>poi_transactions</code> bejegyzés az adott hónapban. M+0 a regisztráció hónapja, M+1 a következő, stb.
        </div>
      </div>

      {/* ── HAVI ÚJ FIZETŐ + COHORT LTV ───────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 14, color: C.muted, margin: '0 0 8px 0' }}>📈 Új fizető user-ek havonta (first purchase)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlyChartData}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke={C.muted} fontSize={11} />
              <YAxis stroke={C.muted} fontSize={11} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: C.panel, border: '1px solid ' + C.border, fontSize: 12 }}
                labelStyle={{ color: C.text }}
                formatter={(v, _n, p) => [v + ' user', p.payload.fullMonth]} />
              <Bar dataKey="count" fill={C.accent} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 14, color: C.muted, margin: '0 0 8px 0' }}>💰 Cohort LTV</h3>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: C.muted, position: 'sticky', top: 0, background: C.card2 }}>
                  <th style={{ padding: '4px', textAlign: 'left' }}>Cohort</th>
                  <th style={{ padding: '4px', textAlign: 'right' }}>Méret</th>
                  <th style={{ padding: '4px', textAlign: 'right' }}>Fizető</th>
                  <th style={{ padding: '4px', textAlign: 'right' }}>%</th>
                  <th style={{ padding: '4px', textAlign: 'right' }}>Bevétel</th>
                  <th style={{ padding: '4px', textAlign: 'right' }}>LTV/user</th>
                  <th style={{ padding: '4px', textAlign: 'right' }}>LTV/fizető</th>
                </tr>
              </thead>
              <tbody>
                {sub.cohortLtv.map(c => (
                  <tr key={c.cohort} style={{ borderTop: '1px solid ' + C.border }}>
                    <td style={{ padding: '4px', fontWeight: 600 }}>{c.cohort}</td>
                    <td style={{ padding: '4px', textAlign: 'right' }}>{c.size}</td>
                    <td style={{ padding: '4px', textAlign: 'right', color: c.paid > 0 ? C.ok : C.muted }}>{c.paid}</td>
                    <td style={{ padding: '4px', textAlign: 'right', color: C.muted }}>{c.paidPct}%</td>
                    <td style={{ padding: '4px', textAlign: 'right', color: c.revenueUsd > 0 ? C.accent : C.muted }}>{fmtUsd(c.revenueUsd)}</td>
                    <td style={{ padding: '4px', textAlign: 'right', color: C.muted }}>{fmtUsd(c.avgLtvUsd)}</td>
                    <td style={{ padding: '4px', textAlign: 'right', color: C.accent }}>{fmtUsd(c.avgLtvPayingUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── 🆕 Sprint 5: PER-PRODUCT BONTÁS ─────────────────────────────────── */}
      {data.productBreakdown && data.productBreakdown.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, color: C.muted, margin: '0 0 12px 0' }}>📦 Termék-bontás</h3>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: 'left', borderBottom: '1px solid ' + C.border }}>
                <th style={{ padding: '6px 4px' }}>Termék</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Ár</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Tranzakciók</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Egyedi user</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Vásárlás/user</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Bevétel</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Átlag bevétel/user</th>
              </tr>
            </thead>
            <tbody>
              {data.productBreakdown.map(p => (
                <tr key={p.productId} style={{ borderBottom: '1px solid ' + C.border }}>
                  <td style={{ padding: '6px 4px', fontWeight: 600 }}>{p.productId}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', color: C.muted }}>{fmtUsd(p.priceUsd)}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right' }}>{p.transactions}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', color: C.ok }}>{p.uniqueUsers}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', color: C.muted }}>{p.avgPurchasesPerUser}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', color: C.accent, fontWeight: 600 }}>{fmtUsd(p.totalRevenueUsd)}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', color: C.accent }}>{fmtUsd(p.avgRevenuePerUser)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 10, fontSize: 11, color: C.muted }}>
            💡 „Vásárlás/user" 1.0 fölött = visszatérő vásárlók (a user több poi csomagot vett). Hasznos jel a magasabb LTV-re.
          </div>
        </div>
      )}

      {/* ── 🆕 Sprint 5: VALÓS HAVI CHURN (ha subscription_events elérhető) ── */}
      {data.subscriptionEvents && data.subscriptionEvents.available && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, color: C.muted, margin: '0 0 12px 0' }}>🔄 Valós havi churn (subscription_events alapján — {data.subscriptionEvents.totalEvents} esemény)</h3>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: 'left', borderBottom: '1px solid ' + C.border }}>
                <th style={{ padding: '6px 4px' }}>Hónap</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Új aktív 🟢</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Reaktivált ♻️</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Churn 🔴</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Nettó</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map(m => {
                const s = data.subscriptionEvents.monthlyStats[m] || { newActive: 0, churned: 0, reactivated: 0 };
                const net = s.newActive + s.reactivated - s.churned;
                return (
                  <tr key={m} style={{ borderBottom: '1px solid ' + C.border }}>
                    <td style={{ padding: '6px 4px', fontWeight: 600 }}>{m}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: s.newActive > 0 ? C.ok : C.muted }}>{s.newActive}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: s.reactivated > 0 ? C.accent : C.muted }}>{s.reactivated}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', color: s.churned > 0 ? C.err : C.muted }}>{s.churned}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600, color: net > 0 ? C.ok : net < 0 ? C.err : C.muted }}>
                      {net > 0 ? '+' + net : net}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data.subscriptionEvents && !data.subscriptionEvents.available && (
        <div className="card" style={{ marginBottom: 20, background: '#1a1d28', borderLeft: '3px solid ' + C.warn }}>
          <div style={{ fontSize: 12, color: C.muted }}>
            <b style={{ color: C.warn }}>🔄 Valós havi churn — még nincs adat</b>
            <div style={{ marginTop: 4 }}>A Firestore <code>subscription_events</code> kollekció üres. A CF patch (lásd README) integrálása után az új subscription-státusz változások ide kerülnek, és ezen a kártyán megjelenik a havi churn / reaktiváció.</div>
          </div>
        </div>
      )}

      {/* ── VÁROS BONTÁS ─────────────────────────────────────────────────── */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ fontSize: 14, color: C.muted, margin: 0 }}>🌍 Város-bontás — top {Math.min(30, cities.length)} ({data.totalUsers} userből)</h3>
          <div style={{ display: 'flex', gap: 4 }}>
            {[['revenue', 'Bevétel'], ['users', 'User'], ['paidConv', 'Konverzió %']].map(([v, label]) => (
              <button key={v} onClick={() => setCitySort(v)}
                style={{ padding: '4px 10px', fontSize: 11, background: citySort === v ? C.accent : C.border, color: citySort === v ? '#000' : C.text, border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: C.muted, textAlign: 'left', borderBottom: '1px solid ' + C.border }}>
              <th style={{ padding: '6px 4px' }}>#</th>
              <th style={{ padding: '6px 4px' }}>Város</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>User</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>30 nap aktív</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>Fizető</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>Konv. %</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>Bevétel</th>
              <th style={{ padding: '6px 4px', textAlign: 'right' }}>ARPU</th>
            </tr>
          </thead>
          <tbody>
            {cities.slice(0, 30).map((c, i) => (
              <tr key={c.city} style={{ borderBottom: '1px solid ' + C.border }}>
                <td style={{ padding: '6px 4px', color: C.muted }}>{i + 1}</td>
                <td style={{ padding: '6px 4px', fontWeight: 600 }}>{c.city}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right' }}>{c.users}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right', color: c.active > 0 ? C.ok : C.muted }}>{c.active}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right', color: c.paidUsers > 0 ? C.ok : C.muted }}>{c.paidUsers}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right', color: C.muted }}>{c.paidConvPct}%</td>
                <td style={{ padding: '6px 4px', textAlign: 'right', color: c.revenueUsd > 0 ? C.accent : C.muted }}>{fmtUsd(c.revenueUsd)}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right', color: C.muted }}>{fmtUsd(c.arpu)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {cities.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: C.muted }}>Nincs city-adat.</div>}
      </div>

      <div style={{ marginTop: 16, fontSize: 11, color: C.muted }}>
        💡 <b>Megjegyzés:</b> a churn snapshot — <code>(everPaid − activeNow) / everPaid</code> — nem havi rate, mert a régi <code>subscriptionStatus</code>-változásokat nem tároljuk historikusan.
        Havi rate-hez egy <code>subscription_events</code> Firestore kollekció-loggal lehetne fejlődni, de jelenleg ez a snapshot a maximum, ami torzítás nélkül kiszámolható.
      </div>
    </div>
  );
}
